import { PanelProps } from '@blueprintjs/core';
import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { refreshDataSources } from 'src/actions/data-sources/action-creators/refreshDataSources';
import { ProjectVirtualGraphToCreate } from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { ProjectResourceDialogFooterProps } from 'src/containers/ProjectResourceDialog/types';
import { ProjectVirtualGraphPanel } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanel';
import { useLocalVirtualGraphOptions } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useLocalVirtualGraphOptionsValue';
import { useLocalVirtualGraph } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useLocalVirtualGraphValue';
import { ProjectVirtualGraphPreviewDialogDataSourceTypeSelectionPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/ProjectVirtualGraphPreviewDialogDataSourceTypeSelectionPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceNativeQuoteCharacterSelector } from 'src/selectors/dataSourceNativeQuoteCharacter';
import { dataSourceSelector } from 'src/selectors/dataSourceSelector';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type ProjectVirtualGraphPreviewDialogOverviewPanelProps = {
  virtualGraph: ProjectVirtualGraph;
  setFooterProps: (footerProps: ProjectResourceDialogFooterProps) => void;
  setErrorText: React.Dispatch<React.SetStateAction<string>>;
};

export const ProjectVirtualGraphPreviewDialogOverviewPanel: React.VFC<
  PanelProps<ProjectVirtualGraphPreviewDialogOverviewPanelProps>
> = ({ openPanel, setErrorText, setFooterProps, virtualGraph }) => {
  const dispatch = useReduxDispatch();
  const isMounted = useIsMounted();

  const connection = useReduxSelector(connectionSelector);

  const [localVirtualGraph, setLocalVirtualGraph] = useLocalVirtualGraph();
  const [localVirtualGraphOptions, setLocalVirtualGraphOptions] =
    useLocalVirtualGraphOptions();

  const dataSource = useReduxSelector(
    dataSourceSelector(
      localVirtualGraph?.dataSourceId || virtualGraph?.dataSourceId
    )
  );
  const nativeQuoteCharacter = useReduxSelector(
    dataSourceNativeQuoteCharacterSelector(
      localVirtualGraph?.dataSourceType || virtualGraph?.dataSourceType || null,
      connection
    )
  );

  const tables = [...(dataSource?.tables || [])];

  const handleSetContents = React.useCallback(
    (
      _id: string,
      update: Partial<ProjectVirtualGraphToCreate>,
      isSettingFrom?: boolean
    ) => {
      // its possible, depending on connection/query speed, that
      // tables that the user considered before (but isnt chosen) finished querying
      // use the `isSettingFrom` value if we're switching between using a custom query and not
      setLocalVirtualGraph((prev) =>
        !prev ||
        (!isSettingFrom &&
          typeof update.from === 'object' &&
          typeof prev.from === 'object' &&
          update.from.value !== prev.from.value)
          ? prev
          : {
              ...prev,
              ...update,
              dateLastUpdated: Date.now(),
            }
      );
    },
    [setLocalVirtualGraph]
  );

  const handleUpdateVirtualGraphOptions = React.useCallback(
    (update: Partial<ProjectVirtualGraphOptions>) => {
      setLocalVirtualGraphOptions((prev) => ({ ...prev, ...update }));
    },
    [setLocalVirtualGraphOptions]
  );

  const handleAddVirtualGraph = React.useCallback(
    ({
      name,
      from,
      fields,
      previewRows,
      allResultsIncluded,
    }: ProjectVirtualGraphToCreate) => {
      setLocalVirtualGraph((prev) =>
        prev
          ? {
              ...prev,
              name,
              from,
              fields,
              previewRows,
              allResultsIncluded,
            }
          : prev
      );
    },
    [setLocalVirtualGraph]
  );

  const handleSetIsUsingCustomQuery = React.useCallback(
    (isUsingCustomQuery: boolean) => {
      if (!localVirtualGraph) {
        return;
      }

      if (isUsingCustomQuery) {
        if (typeof localVirtualGraph.from === 'string') {
          return;
        }

        handleSetContents(
          virtualGraph.id,
          {
            errors: [],
            from: '',
            fields: [],
            previewRows: [],
            allResultsIncluded: false,
          },
          true
        );
      } else {
        if (typeof localVirtualGraph.from === 'object') {
          return;
        }

        handleSetContents(
          virtualGraph.id,
          {
            from: {
              name: '',
              text: '',
              value: '',
            },
            errors: [],
            fields: [],
            previewRows: [],
            allResultsIncluded: false,
          },
          true
        );
      }
    },
    [handleSetContents, localVirtualGraph, virtualGraph.id]
  );

  // this does not remove the virtual graph in this case, but rather just
  // sets the scope (VirtualGraph.from) to empty string
  const handleRemoveVirtualGraph = React.useCallback(
    (_id: string) => {
      setLocalVirtualGraph((prev) =>
        !prev || typeof prev.from !== 'object'
          ? prev
          : {
              ...prev,
              fields: [],
              previewRows: [],
              from: {
                ...prev.from,
                text: '',
                value: '',
                catalog: undefined,
                schema: undefined,
                name: '',
              },
            }
      );
    },
    [setLocalVirtualGraph]
  );

  const handleUpdateDataSource = React.useCallback(() => {
    dispatch(refreshDataSources());
    openPanel({
      renderPanel: ProjectVirtualGraphPreviewDialogDataSourceTypeSelectionPanel,
      props: {
        setFooterProps,
      },
    });
  }, [dispatch, openPanel, setFooterProps]);

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({});
    }
  }, [isMounted, setFooterProps]);

  if (!localVirtualGraph || !localVirtualGraphOptions) {
    return null;
  }

  return (
    <ProjectVirtualGraphPanel
      dataSource={dataSource}
      editedVirtualGraph={localVirtualGraph}
      enablePreviewRefresh
      existingVirtualGraph={virtualGraph}
      handleAddVirtualGraph={handleAddVirtualGraph}
      handleRemoveVirtualGraph={handleRemoveVirtualGraph}
      handleUpdateDataSource={handleUpdateDataSource}
      handleUpdateVirtualGraph={handleSetContents}
      isSettingsOpenKey={localVirtualGraph.isSettingsOpenKey}
      isUsingCustomQuery={typeof localVirtualGraph.from === 'string'}
      nativeQuoteCharacter={nativeQuoteCharacter}
      selectedTab={virtualGraph.id}
      setErrorText={setErrorText}
      setIsUsingCustomQuery={handleSetIsUsingCustomQuery}
      setSelectedTab={noop}
      setVirtualGraphOptions={setLocalVirtualGraphOptions}
      tables={tables}
      updateVirtualGraphOptions={handleUpdateVirtualGraphOptions}
      virtualGraphOptions={localVirtualGraphOptions}
      virtualGraphsToCreate={{ [virtualGraph.id]: localVirtualGraph }}
    />
  );
};
